import { useEffect, useState, type RefObject } from 'react';

const FOCUSABLE =
  'a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"]), [contenteditable="true"]';

export function focusableWithin(container: HTMLElement): HTMLElement[] {
  return Array.from(container.querySelectorAll<HTMLElement>(FOCUSABLE)).filter(
    (el) => el.tabIndex >= 0 && !el.closest('[inert]') && el.getClientRects().length > 0,
  );
}

export interface FocusTrapOptions {
  /** Element to focus first; defaults to the first focusable child, then the container. */
  initialFocus?: RefObject<HTMLElement | null>;
  /** Restore focus to the previously focused element on deactivation (default true). */
  restoreFocus?: boolean;
}

interface Trap {
  root: HTMLElement;
  /** The last element focused inside `root` (restored when a layer above closes and its opener is gone). */
  lastFocused: HTMLElement | null;
}

/** Active traps, oldest first; the last one is the topmost layer and owns Tab. */
const traps: Trap[] = [];

function activeElement(): HTMLElement | null {
  const el = typeof document !== 'undefined' ? document.activeElement : null;
  return el instanceof HTMLElement && el !== document.body ? el : null;
}

/** Tab / Shift+Tab cycle inside the topmost trap; focus that escaped it (to `<body>`, a layer below) is pulled back in. */
function onDocumentKeyDown(event: KeyboardEvent): void {
  if (event.key !== 'Tab' || event.defaultPrevented || event.altKey || event.ctrlKey || event.metaKey) return;
  const top = traps[traps.length - 1];
  if (!top) return;
  const { root } = top;
  const items = focusableWithin(root);
  if (items.length === 0) {
    event.preventDefault();
    if (!root.contains(document.activeElement)) focusContainer(root);
    return;
  }
  const firstItem = items[0];
  const lastItem = items[items.length - 1];
  const current = document.activeElement;
  const inside = root.contains(current);
  if (event.shiftKey && (current === firstItem || !inside)) {
    event.preventDefault();
    lastItem?.focus();
  } else if (!event.shiftKey && (current === lastItem || !inside)) {
    event.preventDefault();
    firstItem?.focus();
  }
}

function onDocumentFocusIn(event: FocusEvent): void {
  const target = event.target;
  if (!(target instanceof HTMLElement)) return;
  for (const trap of traps) if (trap.root.contains(target) && trap.root !== target) trap.lastFocused = target;
}

function addTrap(trap: Trap): void {
  if (traps.length === 0) {
    document.addEventListener('keydown', onDocumentKeyDown);
    document.addEventListener('focusin', onDocumentFocusIn);
  }
  traps.push(trap);
}

function removeTrap(trap: Trap): void {
  const index = traps.indexOf(trap);
  if (index >= 0) traps.splice(index, 1);
  if (traps.length === 0) {
    document.removeEventListener('keydown', onDocumentKeyDown);
    document.removeEventListener('focusin', onDocumentFocusIn);
  }
}

function focusContainer(root: HTMLElement): void {
  if (!root.hasAttribute('tabindex')) root.setAttribute('tabindex', '-1');
  root.focus({ preventScroll: true });
}

function canRestore(el: HTMLElement | null, closing: HTMLElement): el is HTMLElement {
  return el !== null && el.isConnected && !closing.contains(el) && !el.closest('[inert]');
}

/**
 * Where focus goes back to: the element focused before the layer opened if it
 * is still there, else the opener seen when the layer first rendered, else
 * the layer underneath (the element last focused in it, or its first control).
 */
function restoreTarget(closing: HTMLElement, candidates: Array<HTMLElement | null>): HTMLElement | null {
  for (const candidate of candidates) if (canRestore(candidate, closing)) return candidate;
  const below = traps[traps.length - 1];
  if (!below || !below.root.isConnected) return null;
  if (canRestore(below.lastFocused, closing) && below.root.contains(below.lastFocused)) return below.lastFocused;
  return focusableWithin(below.root)[0] ?? below.root;
}

/** Keeps keyboard focus inside `container` while `active` (modal sheets, dialogs, menus). */
export function useFocusTrap(
  container: RefObject<HTMLElement | null>,
  active: boolean,
  { initialFocus, restoreFocus = true }: FocusTrapOptions = {},
): void {
  // The opener as seen while the layer first renders: a child with `autoFocus`
  // takes focus during the commit, before the effect below can look.
  const [opener] = useState(activeElement);

  useEffect(() => {
    const root = container.current;
    if (!active || !root) return;
    const focused = activeElement();
    const previous = focused && !root.contains(focused) ? focused : null;
    const trap: Trap = { root, lastFocused: null };
    addTrap(trap);
    const preferred = initialFocus?.current;
    if (preferred) preferred.focus({ preventScroll: true });
    else if (!focused || !root.contains(focused)) {
      // A child that took focus itself (`autoFocus`) keeps it.
      const first = focusableWithin(root)[0];
      if (first) first.focus({ preventScroll: true });
      else focusContainer(root);
    }
    trap.lastFocused = activeElement();

    return () => {
      removeTrap(trap);
      if (!restoreFocus) return;
      // Focus is elsewhere already (the user clicked outside, a layer above took it).
      const now = activeElement();
      if (now && !root.contains(now)) return;
      restoreTarget(root, [previous, opener])?.focus({ preventScroll: true });
    };
  }, [container, active, initialFocus, restoreFocus, opener]);
}
